import React, { useState, useEffect } from 'react';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { TrendingUp, DollarSign, ShoppingCart, RefreshCw, Download, Calendar, ArrowUpCircle, ArrowDownCircle, Filter } from 'lucide-react';
import { apiClient, formatApiError } from '../context/AuthContext';
import toast from 'react-hot-toast';
import * as XLSX from 'xlsx';

export default function PerformanceAnalytics() {
  const [dateRange, setDateRange] = useState('30days');
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');
  const [kpiData, setKpiData] = useState(null);
  const [salesData, setSalesData] = useState([]);
  const [refundsData, setRefundsData] = useState([]);
  const [transactionsData, setTransactionsData] = useState(null);
  const [transactionFilter, setTransactionFilter] = useState('all'); // 'all', 'credit', 'refund'
  const [loading, setLoading] = useState(false);

  // Calculate date range
  const getDateRange = () => {
    const end = new Date();
    let start = new Date();

    switch (dateRange) {
      case 'today':
        start = new Date(end.getFullYear(), end.getMonth(), end.getDate());
        break;
      case '7days':
        start.setDate(end.getDate() - 7);
        break;
      case '30days':
        start.setDate(end.getDate() - 30);
        break;
      case 'month':
        start = new Date(end.getFullYear(), end.getMonth(), 1);
        break;
      case 'year':
        start = new Date(end.getFullYear(), 0, 1);
        break;
      case 'custom':
        if (customStart && customEnd) {
          return {
            start: new Date(customStart).toISOString(),
            end: new Date(customEnd).toISOString()
          };
        }
        return null;
      default:
        start.setDate(end.getDate() - 30);
    }

    return {
      start: start.toISOString(),
      end: end.toISOString()
    };
  };

  // Fetch all analytics data
  const fetchAnalytics = async () => {
    const range = getDateRange();
    if (!range) {
      toast.error('Veuillez sélectionner une période personnalisée valide');
      return;
    }

    setLoading(true);
    try {
      const params = `?start_date=${range.start}&end_date=${range.end}`;

      // Fetch KPI
      const kpiRes = await apiClient.get(`/analytics/kpi${params}`);
      setKpiData(kpiRes.data);

      // Fetch Sales Data
      const grouping = dateRange === 'year' ? 'month' : dateRange === 'month' ? 'week' : 'day';
      const salesRes = await apiClient.get(`/analytics/sales${params}&grouping=${grouping}`);
      setSalesData(salesRes.data);

      // Fetch Refunds
      const refundsRes = await apiClient.get(`/analytics/refunds${params}`);
      setRefundsData(refundsRes.data);

      // Fetch Transactions (credits + refunds)
      const transactionsRes = await apiClient.get(`/analytics/transactions${params}`);
      setTransactionsData(transactionsRes.data);

    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAnalytics();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateRange]);

  // Export to CSV
  const exportToCSV = async () => {
    try {
      const range = getDateRange();
      if (!range) return;

      const params = `?start_date=${range.start}&end_date=${range.end}&format=csv`;
      const response = await apiClient.get(`/analytics/export${params}`, {
        responseType: 'blob'
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `ventes_joey_cyan_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();

      toast.success('Export CSV téléchargé !');
    } catch (error) {
      toast.error('Erreur lors de l\'export CSV');
    }
  };

  // Export to Excel
  const exportToExcel = async () => {
    try {
      const range = getDateRange();
      if (!range) return;

      const params = `?start_date=${range.start}&end_date=${range.end}&format=json`;
      const response = await apiClient.get(`/analytics/export${params}`);
      const data = response.data;

      // Create workbook and worksheet
      const ws = XLSX.utils.json_to_sheet(data);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Ventes');

      // Generate Excel file
      XLSX.writeFile(wb, `ventes_joey_cyan_${new Date().toISOString().split('T')[0]}.xlsx`);

      toast.success('Export Excel téléchargé !');
    } catch (error) {
      toast.error('Erreur lors de l\'export Excel');
    }
  };

  // KPI Card Component
  const KPICard = ({ title, value, icon: Icon, color, subtitle }) => (
    <div className="bg-white rounded-2xl border border-gray-100 p-6 shadow-sm hover:shadow-md transition-shadow">
      <div className="flex items-start justify-between mb-3">
        <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${color}`}>
          <Icon size={24} className="text-white" />
        </div>
      </div>
      <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">{title}</h3>
      <p className="text-2xl font-black text-gray-900 mb-1">{value}</p>
      {subtitle && <p className="text-xs text-gray-400">{subtitle}</p>}
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Header with filters */}
      <div className="bg-white rounded-2xl border border-gray-100 p-6 shadow-sm">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div>
            <h2 className="text-xl font-black text-gray-900 mb-1">Suivi de Performance</h2>
            <p className="text-sm text-gray-500">Analyse des ventes et rentabilité</p>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            {/* Quick filters */}
            <button
              onClick={() => setDateRange('today')}
              className={`px-4 py-2 rounded-lg text-sm font-semibold transition-colors ${
                dateRange === 'today' ? 'bg-[#00aaff] text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              Aujourd'hui
            </button>
            <button
              onClick={() => setDateRange('7days')}
              className={`px-4 py-2 rounded-lg text-sm font-semibold transition-colors ${
                dateRange === '7days' ? 'bg-[#00aaff] text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              7 jours
            </button>
            <button
              onClick={() => setDateRange('30days')}
              className={`px-4 py-2 rounded-lg text-sm font-semibold transition-colors ${
                dateRange === '30days' ? 'bg-[#00aaff] text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              30 jours
            </button>
            <button
              onClick={() => setDateRange('month')}
              className={`px-4 py-2 rounded-lg text-sm font-semibold transition-colors ${
                dateRange === 'month' ? 'bg-[#00aaff] text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              Ce mois
            </button>
            <button
              onClick={() => setDateRange('year')}
              className={`px-4 py-2 rounded-lg text-sm font-semibold transition-colors ${
                dateRange === 'year' ? 'bg-[#00aaff] text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              Cette année
            </button>

            {/* Custom date range */}
            <div className="flex items-center gap-2 border-l pl-3">
              <Calendar size={16} className="text-gray-400" />
              <input
                type="date"
                value={customStart}
                onChange={(e) => {
                  setCustomStart(e.target.value);
                  setDateRange('custom');
                }}
                className="border border-gray-200 rounded-lg px-3 py-2 text-xs outline-none focus:border-[#00aaff]"
              />
              <span className="text-gray-400">→</span>
              <input
                type="date"
                value={customEnd}
                onChange={(e) => {
                  setCustomEnd(e.target.value);
                  setDateRange('custom');
                }}
                className="border border-gray-200 rounded-lg px-3 py-2 text-xs outline-none focus:border-[#00aaff]"
              />
              {dateRange === 'custom' && (
                <button
                  onClick={fetchAnalytics}
                  className="bg-[#00aaff] text-white px-4 py-2 rounded-lg text-xs font-semibold hover:bg-[#0088dd] transition-colors"
                >
                  Appliquer
                </button>
              )}
            </div>

            {/* Refresh button */}
            <button
              onClick={fetchAnalytics}
              disabled={loading}
              className="p-2 rounded-lg bg-gray-100 hover:bg-gray-200 transition-colors disabled:opacity-50"
            >
              <RefreshCw size={18} className={`text-gray-600 ${loading ? 'animate-spin' : ''}`} />
            </button>
          </div>
        </div>
      </div>

      {loading && !kpiData ? (
        <div className="flex items-center justify-center py-20">
          <div className="w-8 h-8 border-2 border-[#00aaff] border-t-transparent rounded-full animate-spin" />
        </div>
      ) : (
        <>
          {/* KPI Cards */}
          {kpiData && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <KPICard
                title="CA Brut"
                value={`${kpiData.gross_revenue.toLocaleString('fr-FR')} €`}
                icon={DollarSign}
                color="bg-gradient-to-br from-blue-500 to-blue-600"
                subtitle="Total des ventes"
              />
              <KPICard
                title="CA Net"
                value={`${kpiData.net_revenue.toLocaleString('fr-FR')} €`}
                icon={TrendingUp}
                color="bg-gradient-to-br from-green-500 to-green-600"
                subtitle={`Après ${kpiData.total_refunded.toFixed(2)} € de remboursements`}
              />
              <KPICard
                title="Panier Moyen"
                value={`${kpiData.avg_order_value.toFixed(2)} €`}
                icon={ShoppingCart}
                color="bg-gradient-to-br from-purple-500 to-purple-600"
                subtitle="Par commande"
              />
              <KPICard
                title="Volume"
                value={kpiData.total_orders}
                icon={ShoppingCart}
                color="bg-gradient-to-br from-orange-500 to-orange-600"
                subtitle="Commandes totales"
              />
            </div>
          )}

          {/* Charts */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Revenue Chart */}
            <div className="bg-white rounded-2xl border border-gray-100 p-6 shadow-sm">
              <h3 className="text-lg font-black text-gray-900 mb-4">Évolution du Chiffre d'Affaires</h3>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={salesData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="date" tick={{ fontSize: 12 }} stroke="#999" />
                  <YAxis tick={{ fontSize: 12 }} stroke="#999" />
                  <Tooltip
                    contentStyle={{ borderRadius: '8px', border: '1px solid #e5e5e5' }}
                    formatter={(value) => `${value.toFixed(2)} €`}
                  />
                  <Legend />
                  <Line type="monotone" dataKey="revenue" name="CA (€)" stroke="#00aaff" strokeWidth={3} dot={{ r: 4 }} />
                </LineChart>
              </ResponsiveContainer>
            </div>

            {/* Orders Chart */}
            <div className="bg-white rounded-2xl border border-gray-100 p-6 shadow-sm">
              <h3 className="text-lg font-black text-gray-900 mb-4">Volume des Ventes</h3>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={salesData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="date" tick={{ fontSize: 12 }} stroke="#999" />
                  <YAxis tick={{ fontSize: 12 }} stroke="#999" />
                  <Tooltip contentStyle={{ borderRadius: '8px', border: '1px solid #e5e5e5' }} />
                  <Legend />
                  <Bar dataKey="orders" name="Commandes" fill="#00aaff" radius={[8, 8, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Transactions Section (Credits + Refunds) */}
          <div className="bg-white rounded-2xl border border-gray-100 p-6 shadow-sm">
            <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4 mb-4">
              <div>
                <h3 className="text-lg font-black text-gray-900">Mouvements Financiers</h3>
                {transactionsData && (
                  <p className="text-sm text-gray-500 mt-1">
                    Solde: <span className={`font-bold ${transactionsData.balance >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {transactionsData.balance.toLocaleString('fr-FR')} €
                    </span>
                    <span className="mx-2">•</span>
                    Crédits: <span className="text-green-600 font-semibold">{transactionsData.total_credits.toLocaleString('fr-FR')} €</span>
                    <span className="mx-2">•</span>
                    Remboursements: <span className="text-red-600 font-semibold">{transactionsData.total_refunds.toLocaleString('fr-FR')} €</span>
                  </p>
                )}
              </div>
              <div className="flex items-center gap-2">
                <Filter size={16} className="text-gray-400" />
                <select
                  value={transactionFilter}
                  onChange={(e) => setTransactionFilter(e.target.value)}
                  className="border border-gray-200 rounded-lg px-3 py-2 text-sm font-medium focus:outline-none focus:border-[#00aaff]"
                >
                  <option value="all">Tous les mouvements</option>
                  <option value="credit">Crédits uniquement</option>
                  <option value="refund">Remboursements uniquement</option>
                </select>
              </div>
            </div>

            {transactionsData && transactionsData.transactions.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-100">
                      <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase">Type</th>
                      <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase">Date</th>
                      <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase">Heure</th>
                      <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase">N° Commande</th>
                      <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase">Client</th>
                      <th className="text-right py-3 px-4 text-xs font-semibold text-gray-500 uppercase">Montant</th>
                      <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase">Description</th>
                    </tr>
                  </thead>
                  <tbody>
                    {transactionsData.transactions
                      .filter(t => transactionFilter === 'all' || t.type === transactionFilter)
                      .map((transaction, idx) => (
                      <tr key={idx} className="border-b border-gray-50 hover:bg-gray-50">
                        <td className="py-3 px-4">
                          {transaction.type === 'credit' ? (
                            <span className="flex items-center gap-1 text-green-600 font-semibold">
                              <ArrowUpCircle size={16} /> Crédit
                            </span>
                          ) : (
                            <span className="flex items-center gap-1 text-red-600 font-semibold">
                              <ArrowDownCircle size={16} /> Remboursement
                            </span>
                          )}
                        </td>
                        <td className="py-3 px-4 text-gray-600">{transaction.date}</td>
                        <td className="py-3 px-4 text-gray-500">{transaction.time || '-'}</td>
                        <td className="py-3 px-4 font-semibold text-gray-900">{transaction.order_number}</td>
                        <td className="py-3 px-4 text-gray-600">{transaction.customer}</td>
                        <td className={`py-3 px-4 text-right font-bold ${transaction.type === 'credit' ? 'text-green-600' : 'text-red-600'}`}>
                          {transaction.type === 'credit' ? '+' : '-'}{transaction.amount.toFixed(2)} €
                        </td>
                        <td className="py-3 px-4 text-gray-500 text-xs max-w-xs truncate">{transaction.description}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="text-center text-gray-400 py-8">Aucun mouvement sur cette période</p>
            )}
          </div>

          {/* Export Section */}
          <div className="bg-gradient-to-br from-blue-50 to-purple-50 rounded-2xl border border-blue-100 p-6">
            <div className="flex flex-col md:flex-row items-center justify-between gap-4">
              <div>
                <h3 className="text-lg font-black text-gray-900 mb-1">Export Comptable</h3>
                <p className="text-sm text-gray-600">Téléchargez les données au format CSV ou Excel (HT, TVA, TTC)</p>
              </div>
              <div className="flex gap-3">
                <button
                  onClick={exportToCSV}
                  className="flex items-center gap-2 bg-white text-gray-900 px-5 py-3 rounded-xl font-bold text-sm hover:bg-gray-50 transition-colors border border-gray-200 shadow-sm"
                >
                  <Download size={18} />
                  Export CSV
                </button>
                <button
                  onClick={exportToExcel}
                  className="flex items-center gap-2 bg-[#00aaff] text-white px-5 py-3 rounded-xl font-bold text-sm hover:bg-[#0088dd] transition-colors shadow-sm"
                >
                  <Download size={18} />
                  Export Excel
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
